import { Controller } from "@hotwired/stimulus";

// Segmented toggle between "Filters" and "AI Search" modes on the talent
// index filter card. Persists the pick to sessionStorage so a page refresh
// stays in the same mode. The AI button is optional (only rendered when
// Ai.enabled?) so the controller gracefully no-ops if the target is
// missing.
//
// Server-picked initial mode via `data-initial-mode` on the card takes
// precedence over sessionStorage:
//   "ai"      — AI results just landed OR user was last in AI mode.
//               Keep AI bar visible; hide the conventional filter body.
//               The filter form still renders in the DOM with values
//               populated by the AI mapping — it just isn't shown.
//   "filters" — default; may be overridden by sessionStorage="ai".
//
// The card CSS hides both bodies until this controller marks the card
// as .fm-ready, so no filter/AI paint appears before the correct mode
// is decided — eliminating the flicker on AI-result redirects.
const STORAGE_KEY = "tl-talent-filter-mode";

export default class extends Controller {
    static targets = ["filtersBody", "aiBody", "filtersBtn", "aiBtn"]

    connect() {
        const initial = this.element.dataset.initialMode;
        if (initial === "ai" && this.hasAiBtnTarget) {
            this.showAi({ focus: false });
        } else {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved === "ai" && this.hasAiBtnTarget) {
                this.showAi({ focus: false });
            } else {
                this.showFilters();
            }
        }
        // Reveal the card body now that the correct mode is in place.
        // See `.tl-filter-card:not(.fm-ready) .tl-filter-body/.tl-filter-ai-mode`
        // in the SCSS — those are `visibility: hidden` until this fires.
        this.element.classList.add("fm-ready");
    }

    showFilters() {
        if (this.hasFiltersBodyTarget) this.filtersBodyTarget.style.display = "";
        if (this.hasAiBodyTarget)      this.aiBodyTarget.style.display = "none";
        this._setActive(this.hasFiltersBtnTarget ? this.filtersBtnTarget : null,
                        this.hasAiBtnTarget ? this.aiBtnTarget : null);
        sessionStorage.setItem(STORAGE_KEY, "filters");
    }

    showAi(opts) {
        if (!this.hasAiBodyTarget) return;
        if (this.hasFiltersBodyTarget) this.filtersBodyTarget.style.display = "none";
        this.aiBodyTarget.style.display = "";
        this._setActive(this.hasAiBtnTarget ? this.aiBtnTarget : null,
                        this.hasFiltersBtnTarget ? this.filtersBtnTarget : null);
        sessionStorage.setItem(STORAGE_KEY, "ai");
        // Focus only when user-triggered (click), not on initial mount from
        // an existing URL — else pages loaded with ?ai_query= steal focus.
        if (opts && opts.focus !== false) {
            const input = this.aiBodyTarget.querySelector('input[type="text"]');
            if (input) input.focus();
        }
    }

    _setActive(activeBtn, inactiveBtn) {
        if (activeBtn) {
            activeBtn.classList.add("is-active");
            activeBtn.setAttribute("aria-selected", "true");
        }
        if (inactiveBtn) {
            inactiveBtn.classList.remove("is-active");
            inactiveBtn.setAttribute("aria-selected", "false");
        }
    }
}
